import { useEffect, useState, useCallback } from "react";
import { Check, Ban, Mail, Download, FilePlus2, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { apiError } from "../lib/auth";
import { useFlash } from "../lib/flash";
import { fmtShortDate } from "../lib/format";
import { INVOICE_STATUS, krOre, downloadPdf } from "../lib/billing";
import { Pill } from "./bits";

function InvoiceRow({ inv, busy, act }) {
  const [cls, label] = INVOICE_STATUS[inv.status] || ["draft", inv.status];
  const mailFailed = inv.email?.status === "failed" || inv.paid_email?.status === "failed";
  return (
    <div className="oi-row" data-testid={`owner-invoice-${inv.number}`}>
      <div className="oi-top">
        <b>#{inv.number}</b>
        <Pill status={cls} label={label} />
        <span className="muted">{inv.period_label}</span>
        <span className="oi-amt">{krOre(inv.total_ore)}</span>
      </div>
      <div className="oi-sub muted">
        Forfald {fmtShortDate(inv.due_date)}
        {inv.email?.status === "sent" && ` · mail sendt ${fmtShortDate(inv.email.at)}`}
        {inv.paid_at && ` · betalt ${fmtShortDate(inv.paid_at)}`}
      </div>
      {mailFailed && (
        <div className="oi-fail" data-testid={`owner-invoice-mailfail-${inv.number}`}>
          <AlertTriangle size={14} /> Mail fejlede{inv.email?.error ? `: ${inv.email.error}` : ""}
        </div>
      )}
      <div className="oi-acts">
        {inv.status !== "paid" && inv.status !== "void" && (
          <button className="btn small jatak" disabled={busy} onClick={() => act(inv, "paid")} data-testid={`owner-invoice-paid-${inv.number}`}>
            <Check size={14} /> Markér betalt
          </button>
        )}
        {inv.status !== "void" && (
          <button className={"btn small" + (mailFailed ? " primary" : "")} disabled={busy} onClick={() => act(inv, "email")} data-testid={`owner-invoice-email-${inv.number}`}>
            <Mail size={14} /> {mailFailed ? "Mail fejlede – send igen" : "Send mail igen"}
          </button>
        )}
        <button className="btn small" onClick={() => downloadPdf(`/owner/invoices/${inv.id}/pdf`, inv.number)} data-testid={`owner-invoice-pdf-${inv.number}`}>
          <Download size={14} /> Hent PDF
        </button>
        {(inv.status === "unpaid" || inv.status === "overdue") && (
          <button className="btn small" disabled={busy} onClick={() => act(inv, "void")} data-testid={`owner-invoice-void-${inv.number}`}>
            <Ban size={14} /> Annullér
          </button>
        )}
      </div>
    </div>
  );
}

export default function OwnerInvoices({ store, onChanged }) {
  const flash = useFlash();
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/owner/stores/${store.id}/invoices`).then((r) => setList(r.data.invoices)), [store.id]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      const r = await fn();
      if (r?.data?.flash) flash(r.data.flash);
      await load();
      onChanged?.();
    } catch (e) {
      flash(apiError(e.response?.data?.detail) || e.message, "error");
      load();
    } finally {
      setBusy(false);
    }
  };
  const act = (inv, action) =>
    run(() => api.post(`/owner/invoices/${inv.id}/${action}`), action === "void" ? `Annullér faktura ${inv.number}?` : null);

  return (
    <div style={{ display: "grid", gap: 10 }} data-testid="owner-invoices">
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button className="btn small" disabled={busy} onClick={() => run(() => api.post(`/owner/stores/${store.id}/invoices`), "Opret og send en ny faktura nu?")} data-testid="owner-invoice-create">
          <FilePlus2 size={15} /> Opret faktura nu
        </button>
        {store.plan !== "cancelled" && (
          <button className="btn small" disabled={busy} onClick={() => run(() => api.post(`/owner/stores/${store.id}/cancel`), "Stop abonnementet? Der oprettes ikke flere fakturaer.")} data-testid="owner-cancel-subscription">
            <Ban size={15} /> Stop abonnement
          </button>
        )}
      </div>
      {store.invoice_pending && <div className="notice warn" style={{ margin: 0 }}>Faktura venter på bankoplysninger i Fakturaindstillinger.</div>}
      {!list ? <div className="muted">Indlæser…</div> : list.length ? list.map((inv) => <InvoiceRow key={inv.id} inv={inv} busy={busy} act={act} />)
        : <div className="muted" style={{ fontSize: 14 }}>Ingen fakturaer endnu.</div>}
    </div>
  );
}
